import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Button, Space, Switch, Typography } from "antd";
import { observer } from "mobx-react-lite";

import { SubscriptionInfo } from "../components/subscription/SubscriptionInfo";
import { TrialBanner } from "../components/subscription/TrialBanner";
import { userStore } from "../stores/userStore";

import type { PaymentMethod, SubscriptionData } from "../api/subscription";

const ALLOWED_HOSTS = ["localhost", "127.0.0.1", "my.nextgis.dev"];

const PAYMENT_METHODS: { label: string; value: PaymentMethod | null }[] = [
  { label: "None", value: null },
  {
    label: "Visa ····4242",
    value: {
      type: "card",
      status: "success",
      card: { brand: "visa", last4: "4242", exp_month: 12, exp_year: 2027 },
    },
  },
  {
    label: "Mastercard ····1234",
    value: {
      type: "card",
      status: "success",
      card: {
        brand: "mastercard",
        last4: "1234",
        exp_month: 8,
        exp_year: 2026,
      },
    },
  },
  {
    label: "PayPal",
    value: { type: "paypal", status: "success" },
  },
];

const PLANS: {
  id: string;
  price: string;
  label: string;
  user_settings: {
    user_limit: number | null;
    storage_limit: number | null;
    resource_limit: number | null;
  };
}[] = [
  {
    id: "lemon",
    price: "0.00",
    label: "Free",
    user_settings: {
      user_limit: 1,
      storage_limit: 5 * 1024 ** 3,
      resource_limit: 15,
    },
  },
  {
    id: "mandarin",
    price: "5.00",
    label: "Mini",
    user_settings: {
      user_limit: 10,
      storage_limit: 10 * 1024 ** 3,
      resource_limit: null,
    },
  },
  {
    id: "pomelo",
    price: "10.00",
    label: "Premium",
    user_settings: {
      user_limit: null,
      storage_limit: 50 * 1024 ** 3,
      resource_limit: null,
    },
  },
];

const BASE_SUB: SubscriptionData = {
  autopayment: true,
  plan: "lemon",
  interval: "monthly",
  price: "0.00",
  currency: "USD",
  expire_at: null,
  options: null,
  canceled_at: null,
  trial_active: false,
  allow_trial: true,
  user_settings: PLANS[0].user_settings,
};

export const TestSubscriptionInfoPage = observer(
  function TestSubscriptionInfoPage() {
    useEffect(() => {
      userStore.setSubscription({ ...BASE_SUB });
    }, []);

    if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
      return <Navigate to="/" replace />;
    }

    const sub = userStore.subscription ?? BASE_SUB;

    const setPlan = (planId: string) => {
      const plan = PLANS.find((p) => p.id === planId)!;
      userStore.setSubscription({
        ...sub,
        plan: planId,
        price: plan.price,
        trial_active: false,
        allow_trial: planId === "lemon",
        user_settings: plan.user_settings,
      });
    };

    const setTrialActive = (value: boolean) => {
      userStore.setSubscription({ ...sub, trial_active: value });
    };

    const setInterval_ = (value: string) => {
      userStore.setSubscription({ ...sub, interval: value });
    };

    const setAutopayment = (value: boolean) => {
      userStore.setSubscription({ ...sub, autopayment: value });
    };

    const setPaymentMethod = (value: PaymentMethod | null) => {
      userStore.setSubscription({ ...sub, payment_method: value });
    };

    return (
      <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px" }}>
        <Typography.Title level={4} style={{ marginBottom: 24 }}>
          SubscriptionInfo test
        </Typography.Title>

        <Space
          direction="vertical"
          size={16}
          style={{ width: "100%", marginBottom: 32 }}
        >
          <div>
            <Typography.Text type="secondary">Plan</Typography.Text>
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              {PLANS.map((p) => (
                <Button
                  key={p.id}
                  type={
                    sub.plan?.toLowerCase() === p.id ? "primary" : "default"
                  }
                  onClick={() => setPlan(p.id)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Typography.Text type="secondary">Interval</Typography.Text>
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              {["monthly", "yearly"].map((iv) => (
                <Button
                  key={iv}
                  type={sub.interval === iv ? "primary" : "default"}
                  disabled={sub.plan?.toLowerCase() === "lemon"}
                  onClick={() => setInterval_(iv)}
                >
                  {iv}
                </Button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch
              checked={sub.autopayment}
              onChange={setAutopayment}
              disabled={sub.plan?.toLowerCase() === "lemon"}
            />
            <Typography.Text>autopayment</Typography.Text>
          </div>

          <div>
            <Typography.Text type="secondary">Payment method</Typography.Text>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {PAYMENT_METHODS.map((pm) => (
                <Button
                  key={pm.label}
                  type={
                    JSON.stringify(sub.payment_method ?? null) ===
                    JSON.stringify(pm.value)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => setPaymentMethod(pm.value)}
                >
                  {pm.label}
                </Button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch
              checked={sub.trial_active}
              onChange={setTrialActive}
              disabled={sub.plan?.toLowerCase() !== "lemon"}
            />
            <Typography.Text>trial_active</Typography.Text>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch checked={sub.allow_trial} disabled />
            <Typography.Text type="secondary">
              allow_trial (auto)
            </Typography.Text>
          </div>
        </Space>

        <TrialBanner />
        <SubscriptionInfo />
      </div>
    );
  },
);
